<script lang="ts" setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Cookies from 'js-cookie';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue';
import { COOKIE_KEY_LANGUAGE } from '@/utils/constants';
import {
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuRoot,
  NavigationMenuTrigger,
  NavigationMenuViewport,
  // NavigationMenuLink,
} from 'reka-ui';
import MenuProject from './MenuProject.vue';
import MenuEvents from './MenuEvents.vue';
import MenuJournalism from './MenuJournalism.vue';
import MenuInformation from './MenuInformation.vue';
import MenuCooperation from './MenuCooperation.vue';
import MenuAbout from './MenuAbout.vue';

const { availableLocales, locale } = useI18n();

const isMobile = ref(false);

// 移动端抽屉菜单开合状态（纯客户端交互，SSR 恒为 false，无 hydration 差异）
const mobileMenuOpen = ref(false);

const closeMobileMenu = () => {
  mobileMenuOpen.value = false;
};

// 路由变化（点击抽屉内链接跳转）时自动收起抽屉
const route = useRoute();
watch(() => route.path, closeMobileMenu);

const languages = computed(() => availableLocales.slice().sort((a) => {
  return a === locale.value ? -1 : 1;
}));

const changeLanguage = (lang: string) => {
  Cookies.set(COOKIE_KEY_LANGUAGE, lang, {
    expires: 3650,
    path: '/',
    sameSite: 'Lax',
  });
  window.location.href = '/';
}

onMounted(() => {
  const userAgent = navigator.userAgent;
  isMobile.value = /iphone|ipad|ipod|android|OpenHarmony/i.test(userAgent);
});
</script>

<template>
  <header
    class="page-header relative h-72 bg-[rgba(255,255,255,0.6)] text-#2d2d2d shadow-[inset_0_-1px_0_0_#e6e7ec] transition-all duration-200 hover:bg-white hover:shadow-[inset_0_-1px_0_0_#e6e7ec,0_0_10px_0_rgba(0,0,0,0.06)]"
  >
    <div class="mx-auto h-full flex-y-center <sm:px-16 <xl:px-24" :class="$route.path === '/' ? 'max-w-1200' : 'max-w-1360 <2xl:max-w-1200'">
      <div class="h-40 w-189">
        <RouterLink to="/" class="block h-full w-full">
          <img src="@/assets/common/logo-with-title.svg" alt="LOGO" class="block h-full w-full object-contain">
        </RouterLink>
      </div>

      <NavigationMenuRoot :disable-click-trigger="!isMobile" :delay-duration="100" class="ml-25 h-full flex-grow text-16 <lg:hidden first:*:h-full">
        <NavigationMenuList class="m-0 h-full flex list-none p-0">
          <NavigationMenuItem>
            <NavigationMenuTrigger class="h-full cursor-pointer border-none bg-transparent px-20 text-16 hover:text-#1180ff">
              {{ $t('header.nav2') }}
            </NavigationMenuTrigger>
            <ClientOnly>
              <NavigationMenuContent>
                <MenuProject />
              </NavigationMenuContent>
            </ClientOnly>
          </NavigationMenuItem>

          <NavigationMenuItem>
            <NavigationMenuTrigger class="h-full cursor-pointer border-none bg-transparent px-20 text-16 hover:text-#1180ff">
              {{ $t('header.nav5.title') }}
            </NavigationMenuTrigger>
            <ClientOnly>
              <NavigationMenuContent>
                <MenuEvents />
              </NavigationMenuContent>
            </ClientOnly>
          </NavigationMenuItem>

          <NavigationMenuItem v-if="locale === 'zh-CN'">
            <NavigationMenuTrigger class="h-full cursor-pointer border-none bg-transparent px-20 text-16 hover:text-#1180ff">
              新闻
            </NavigationMenuTrigger>
            <ClientOnly>
              <NavigationMenuContent>
                <MenuJournalism />
              </NavigationMenuContent>
            </ClientOnly>
          </NavigationMenuItem>

          <NavigationMenuItem v-if="locale === 'zh-CN'">
            <NavigationMenuTrigger class="h-full cursor-pointer border-none bg-transparent px-20 text-16 hover:text-#1180ff">
              信息公开
            </NavigationMenuTrigger>
            <ClientOnly>
              <NavigationMenuContent>
                <MenuInformation />
              </NavigationMenuContent>
            </ClientOnly>
          </NavigationMenuItem>

          <NavigationMenuItem>
            <NavigationMenuTrigger class="h-full cursor-pointer border-none bg-transparent px-20 text-16 hover:text-#1180ff">
              {{ $t('header.nav3') }}
            </NavigationMenuTrigger>
            <ClientOnly>
              <NavigationMenuContent>
                <MenuCooperation />
              </NavigationMenuContent>
            </ClientOnly>
          </NavigationMenuItem>

          <NavigationMenuItem>
            <NavigationMenuTrigger class="h-full cursor-pointer border-none bg-transparent px-20 text-16 hover:text-#1180ff">
              {{ $t('header.nav4') }}
            </NavigationMenuTrigger>
            <ClientOnly>
              <NavigationMenuContent>
                <MenuAbout />
              </NavigationMenuContent>
            </ClientOnly>
          </NavigationMenuItem>

          <!-- <NavigationMenuItem>
            <NavigationMenuLink
              class="h-full flex-center px-20 text-16 text-#2d2d2d no-underline hover:text-#1180ff"
              href="https://openatom.cn/conference/2025-1/index/main/"
              target="_blank"
            >
              2025生态大会
            </NavigationMenuLink>
          </NavigationMenuItem> -->
        </NavigationMenuList>

        <div class="absolute left-0 right-0 top-full z-70 w-full perspective-[2000px] bg-white">
          <NavigationMenuViewport
            class="mx-auto h-[var(--reka-navigation-menu-viewport-height)] max-w-1200 transition-[width,height] duration-300 data-[state=open]:animate-keyframes-fade-in data-[state=open]:animate-duration-500 data-[state=open]:animate-ease"
          />
        </div>
      </NavigationMenuRoot>

      <div class="flex items-center text-14 <lg:hidden">
        <Menu as="div" class="relative" v-slot="{ open }">
          <MenuButton class="flex cursor-pointer items-center border-0 bg-transparent p-0 text-brand-normal hover:text-brand-primary" :class="{ 'text-brand-primary': open }">
            <div class="i-home-icons:world mr-8 size-16"></div>
            {{ $t('languageName', '', { locale }) }}
            <div class="i-home-icons:arrow-down ml-8 size-16 transition-transform" :class="{ 'rotate-180': open }"></div>
          </MenuButton>
          <MenuItems class="absolute left-1/2 z-60 mt-32 w-144 rounded-4 bg-white p-16 -translate-x-1\/2">
            <MenuItem v-slot="{ active }" v-for="lang in languages" :key="lang">
              <a
                class="block text-center leading-24 no-underline siblings:mt-16 hover:text-#1180ff visited:text-#2d2d2d"
                :class="{
                  'text-brand-primary': active || (locale === lang),
                  'text-brand-normal': !(active || (locale === lang)),
                  'font-medium': locale === lang
                }"
                href="javascript:;"
                @click="changeLanguage(lang)"
              >
                {{ $t('languageName', '', { locale: lang }) }}
              </a>
            </MenuItem>
          </MenuItems>
        </Menu>
      </div>

      <!-- 移动端汉堡按钮（仅 <lg 显示） -->
      <button
        type="button"
        class="h-40 w-40 flex-center cursor-pointer border-0 bg-transparent text-brand-normal <lg:ml-auto lg:hidden"
        aria-label="打开菜单"
        :aria-expanded="mobileMenuOpen"
        @click="mobileMenuOpen = !mobileMenuOpen"
      >
        <div class="i-home-icons:menu size-24"></div>
      </button>
    </div>

    <!-- 移动端抽屉菜单（仅 <lg 显示，纵向手风琴） -->
    <div
      v-show="mobileMenuOpen"
      class="absolute inset-x-0 top-full z-70 max-h-[calc(100vh-72px)] overflow-y-auto border-t-1 border-#E6E7EC bg-white lg:hidden"
    >
      <NavigationMenuRoot orientation="vertical" class="px-16 py-8 text-16">
        <NavigationMenuList class="m-0 flex flex-col list-none p-0">
          <NavigationMenuItem value="project" class="border-b-1 border-#F0F0F4">
            <NavigationMenuTrigger class="group w-full flex cursor-pointer items-center justify-between border-none bg-transparent px-8 py-16 text-16 text-#2d2d2d font-500">
              {{ $t('header.nav2') }}
              <div class="i-home-icons:arrow-down size-16 transition-transform group-data-[state=open]:rotate-180"></div>
            </NavigationMenuTrigger>
            <ClientOnly>
              <NavigationMenuContent class="px-8 pb-16">
                <MenuProject />
              </NavigationMenuContent>
            </ClientOnly>
          </NavigationMenuItem>

          <NavigationMenuItem value="events" class="border-b-1 border-#F0F0F4">
            <NavigationMenuTrigger class="group w-full flex cursor-pointer items-center justify-between border-none bg-transparent px-8 py-16 text-16 text-#2d2d2d font-500">
              {{ $t('header.nav5.title') }}
              <div class="i-home-icons:arrow-down size-16 transition-transform group-data-[state=open]:rotate-180"></div>
            </NavigationMenuTrigger>
            <ClientOnly>
              <NavigationMenuContent class="px-8 pb-16">
                <MenuEvents />
              </NavigationMenuContent>
            </ClientOnly>
          </NavigationMenuItem>

          <NavigationMenuItem v-if="locale === 'zh-CN'" value="journalism" class="border-b-1 border-#F0F0F4">
            <NavigationMenuTrigger class="group w-full flex cursor-pointer items-center justify-between border-none bg-transparent px-8 py-16 text-16 text-#2d2d2d font-500">
              新闻
              <div class="i-home-icons:arrow-down size-16 transition-transform group-data-[state=open]:rotate-180"></div>
            </NavigationMenuTrigger>
            <ClientOnly>
              <NavigationMenuContent class="px-8 pb-16">
                <MenuJournalism />
              </NavigationMenuContent>
            </ClientOnly>
          </NavigationMenuItem>

          <NavigationMenuItem v-if="locale === 'zh-CN'" value="information" class="border-b-1 border-#F0F0F4">
            <NavigationMenuTrigger class="group w-full flex cursor-pointer items-center justify-between border-none bg-transparent px-8 py-16 text-16 text-#2d2d2d font-500">
              信息公开
              <div class="i-home-icons:arrow-down size-16 transition-transform group-data-[state=open]:rotate-180"></div>
            </NavigationMenuTrigger>
            <ClientOnly>
              <NavigationMenuContent class="px-8 pb-16">
                <MenuInformation />
              </NavigationMenuContent>
            </ClientOnly>
          </NavigationMenuItem>

          <NavigationMenuItem value="cooperation" class="border-b-1 border-#F0F0F4">
            <NavigationMenuTrigger class="group w-full flex cursor-pointer items-center justify-between border-none bg-transparent px-8 py-16 text-16 text-#2d2d2d font-500">
              {{ $t('header.nav3') }}
              <div class="i-home-icons:arrow-down size-16 transition-transform group-data-[state=open]:rotate-180"></div>
            </NavigationMenuTrigger>
            <ClientOnly>
              <NavigationMenuContent class="px-8 pb-16">
                <MenuCooperation />
              </NavigationMenuContent>
            </ClientOnly>
          </NavigationMenuItem>

          <NavigationMenuItem value="about" class="border-b-1 border-#F0F0F4">
            <NavigationMenuTrigger class="group w-full flex cursor-pointer items-center justify-between border-none bg-transparent px-8 py-16 text-16 text-#2d2d2d font-500">
              {{ $t('header.nav4') }}
              <div class="i-home-icons:arrow-down size-16 transition-transform group-data-[state=open]:rotate-180"></div>
            </NavigationMenuTrigger>
            <ClientOnly>
              <NavigationMenuContent class="px-8 pb-16">
                <MenuAbout />
              </NavigationMenuContent>
            </ClientOnly>
          </NavigationMenuItem>
        </NavigationMenuList>
      </NavigationMenuRoot>

      <!-- 移动端语言切换 -->
      <div class="flex items-center justify-center gap-24 border-t-1 border-#F0F0F4 px-16 py-20 text-14">
        <a
          v-for="lang in languages"
          :key="lang"
          href="javascript:;"
          class="no-underline"
          :class="locale === lang ? 'font-medium text-brand-primary' : 'text-brand-normal'"
          @click="changeLanguage(lang)"
        >
          {{ $t('languageName', '', { locale: lang }) }}
        </a>
      </div>
    </div>
  </header>
</template>

<style lang="scss" scoped>
</style>
